import { ComponentRef, Injectable, inject } from '@angular/core';
import { assert } from '../../../../core/assert/assert';
import { AbsencePlannerEventModel } from '../../../../core/models/absence-planner/absence-planner-event.model';
import { DateTimeHelper } from '../../../calendar/util/date-time-helper';
import { EventSlotDirective } from '../../directives/event-slot.directive';
import { AbsencePlannerService } from '../../services/absence-planner.service';
import { AbsencePlannerEventComponent } from '../absence-planner-event.component';

@Injectable({
  providedIn: 'root',
})
export class AbsencePlannerEventComponentHandlerService {
  private readonly absencePlannerService = inject(AbsencePlannerService);

  public buildComponent(
    plannerEventModel: AbsencePlannerEventModel,
  ): ComponentRef<AbsencePlannerEventComponent> | null {
    const slot = this.querySlot(plannerEventModel);

    // If event is completely outside the planner, container is null so don't add and render it at all.
    if (slot) {
      const updatedPlannerEventModel = new AbsencePlannerEventModel();
      Object.assign(updatedPlannerEventModel, plannerEventModel);

      const componentRef = slot.vcr.createComponent(AbsencePlannerEventComponent);
      this.passPlannerEventParams(componentRef, updatedPlannerEventModel, slot);
      updatedPlannerEventModel.componentRef = componentRef;
      return componentRef;
    }
    return null;
  }

  public removeComponent(plannerEventModel: AbsencePlannerEventModel): void {
    assert(plannerEventModel.componentRef !== null, 'Component ref is null.');
    plannerEventModel.componentRef!.destroy();
    this.absencePlannerService.plannerModel.plannerComponentInstance.cd.detectChanges();
  }

  public updateComponentWhileResizing(
    currentlyDraggedEventModel: AbsencePlannerEventModel,
    isLeftDragHandle: boolean,
    dayDiff: number,
    initialLeft: number,
  ): void {
    const updatedWidth = this.calculatePlannerEventWidth(currentlyDraggedEventModel);
    currentlyDraggedEventModel.componentRef!.setInput('eventWidth', updatedWidth);

    if (isLeftDragHandle) {
      const plannerModel = this.absencePlannerService.plannerModel;
      const left = plannerModel.slotWidth * dayDiff + initialLeft;
      currentlyDraggedEventModel.componentRef!.setInput('eventLeft', left);
    }
  }

  private calculatePlannerEventWidth(plannerEventModel: AbsencePlannerEventModel): number {
    const plannerModel = this.absencePlannerService.plannerModel;
    assert(plannerModel.slotWidth !== null, 'Slot width is null.');

    const duration = DateTimeHelper.eachDayOfInterval(
      plannerEventModel.start,
      plannerEventModel.end,
    ).length;
    assert(duration >= 1, `A planner event must be at least one day long but was: ${duration}`);

    return plannerModel.slotWidth * duration;
  }

  private calculatePlannerEventOutsideLeftBorder(updatedEventModel: AbsencePlannerEventModel) {
    const plannerModel = this.absencePlannerService.plannerModel;
    assert(plannerModel.slotWidth !== null, 'Slot width is null.');

    const start = updatedEventModel.start;
    const end = plannerModel.plannerWeeks[0].startDate;
    let left = 0;
    if (DateTimeHelper.isBefore(start, end)) {
      const dayDiff = DateTimeHelper.eachDayOfInterval(start, end).length - 1;
      left = -Math.floor(plannerModel.slotWidth * dayDiff);
    }
    return left;
  }

  private findSlot(
    plannerEventModel: AbsencePlannerEventModel,
    userAssociatedSlots: EventSlotDirective[],
  ): EventSlotDirective | null {
    const eventStart = plannerEventModel.start;
    const eventEnd = plannerEventModel.end;

    const firstSlot = userAssociatedSlots[0];
    const lastSlot = userAssociatedSlots[userAssociatedSlots.length - 1];

    // Event is completely outside left
    const isOutSideLeft =
      DateTimeHelper.isBefore(eventStart, firstSlot.date()) &&
      DateTimeHelper.isBefore(eventEnd, firstSlot.date());

    // Event is completely outside right
    const isOutSideRight =
      DateTimeHelper.isAfter(eventStart, lastSlot.date()) &&
      DateTimeHelper.isAfter(eventEnd, lastSlot.date());

    // Event is completely outside of planner, so don't render it.
    if (isOutSideLeft || isOutSideRight) {
      return null;
    }

    let container: EventSlotDirective | null = null;
    // Event start date is before first container date
    if (DateTimeHelper.isBefore(plannerEventModel.start, firstSlot.date())) {
      container = firstSlot;
    }

    // Event start date is after last container date
    if (DateTimeHelper.isAfter(plannerEventModel.start, lastSlot.date())) {
      container = lastSlot;
    }

    assert(container !== null && typeof container !== 'undefined', 'Event container is undefined.');

    return container;
  }

  private querySlot(plannerEventModel: AbsencePlannerEventModel): EventSlotDirective {
    const plannerModel = this.absencePlannerService.plannerModel;
    assert(plannerModel.eventSlots !== null, 'PlannerEventSlots are null.');

    const memberId = plannerEventModel.userId!;
    const startDate = DateTimeHelper.format(plannerEventModel.start);

    const userAssociatedSlots = plannerModel.eventSlots.get(memberId)!;

    let slot = userAssociatedSlots.find(
      (slot) => DateTimeHelper.format(slot.date()) === startDate && slot.member().id === memberId,
    );

    if (!slot) {
      slot = this.findSlot(plannerEventModel, userAssociatedSlots)!;
    }
    return slot;
  }

  private passPlannerEventParams(
    componentRef: ComponentRef<AbsencePlannerEventComponent>,
    updatedPlannerEventModel: AbsencePlannerEventModel<AbsencePlannerEventComponent>,
    slot: EventSlotDirective,
  ): void {
    const plannerModel = this.absencePlannerService.plannerModel;

    componentRef.setInput('plannerEventModel', updatedPlannerEventModel);
    componentRef.setInput('eventWidth', this.calculatePlannerEventWidth(updatedPlannerEventModel));
    componentRef.setInput(
      'eventLeft',
      this.calculatePlannerEventOutsideLeftBorder(updatedPlannerEventModel),
    );
    componentRef.setInput('slotWidth', plannerModel.slotWidth);
    componentRef.setInput('eventContainer', slot.eventContainer());
  }
}
